import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, Alert
} from '@mui/material';
import { Stop as StopIcon } from '@mui/icons-material';

export default function ClockOutDialog({
  open, onClose, onClockOut,
  currentClockIn, elapsedTime, formatElapsedTime,
  formatTimeInTimezone, getUserTimezone,
  clockOutBreak, setClockOutBreak,
  clockOutNotes, setClockOutNotes,
  error = '', busy = false
}) {
  // Break punches are measured server-side; the manual field is ignored when
  // they exist, so say so instead of letting someone type a number that does nothing.
  const punchedBreakMinutes = Math.round((currentClockIn?.break_seconds_accum || 0) / 60);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <StopIcon sx={{ mr: 1, color: 'error.main' }} />
          Clock Out
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {/* Shown here because the caller's own error Alert is hidden under
              this dialog's backdrop — a failed clock-out looked like a dead button. */}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {currentClockIn && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Clocked in:</strong> {formatTimeInTimezone(currentClockIn.clock_in_time, getUserTimezone())}
              </Typography>
              <Typography variant="body2">
                <strong>Time worked:</strong> {formatElapsedTime(elapsedTime)}
              </Typography>
              {(currentClockIn.affiliate_name || currentClockIn.company_name) && (
                <Typography variant="body2">
                  <strong>Where:</strong> {currentClockIn.affiliate_name || currentClockIn.company_name}
                </Typography>
              )}
            </Alert>
          )}

          {punchedBreakMinutes > 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{punchedBreakMinutes} min</strong> of break was recorded from your
              break punches. Short rest breaks stay paid — only a meal period of 30
              minutes or more is deducted. The box below is ignored when you punched
              your breaks; if a break was recorded wrong, tell your supervisor.
            </Alert>
          ) : null}

          <TextField
            fullWidth
            label="Break Minutes"
            type="number"
            value={clockOutBreak}
            onChange={(e) => setClockOutBreak(Math.max(0, parseInt(e.target.value) || 0))}
            size="small"
            sx={{ mb: 2 }}
            disabled={punchedBreakMinutes > 0}
            helperText={punchedBreakMinutes > 0
              ? 'Measured break punches are used instead of this number'
              : 'Enter break time to deduct from total hours'}
            InputProps={{
              inputProps: { min: 0 }
            }}
          />

          <TextField
            fullWidth
            label="Notes (Optional)"
            value={clockOutNotes}
            onChange={(e) => setClockOutNotes(e.target.value)}
            size="small"
            multiline
            rows={2}
            placeholder="Work completed, tasks, etc."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onClockOut} variant="contained" color="error" disabled={busy}>
          Clock Out
        </Button>
      </DialogActions>
    </Dialog>
  );
}
